import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PaymentStatus = ({ status, paymentData, routingInfo, onReset }) => {
  const isSuccess = status === 'completed';

  return (
    <div className="space-y-6 text-center">
      {/* Status Icon */}
      <div className="flex justify-center">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
          isSuccess ? 'bg-success/10' : 'bg-error/10'
        }`}>
          <Icon 
            name={isSuccess ? 'CheckCircle' : 'XCircle'} 
            size={48} 
            className={isSuccess ? 'text-success' : 'text-error'}
          />
        </div>
      </div>

      {/* Status Message */}
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">
          {isSuccess ? 'Payment Successful!' : 'Payment Failed'}
        </h3>
        <p className="text-muted-foreground">
          {isSuccess 
            ? 'Your transaction has been confirmed on the blockchain' :'There was an issue processing your payment'}
        </p>
      </div>

      {/* Transaction Details */}
      {isSuccess && paymentData && (
        <div className="p-4 rounded-lg bg-muted/20 border border-border space-y-3 text-left">
          <h4 className="text-sm font-semibold text-foreground mb-3">Transaction Details</h4>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Amount Paid</span>
            <span className="font-medium text-foreground">
              {paymentData?.amount} {paymentData?.selectedCrypto?.toUpperCase()}
            </span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">USD Value</span>
            <span className="font-medium text-foreground">
              ${parseFloat(paymentData?.usdAmount)?.toFixed(2)}
            </span>
          </div>

          {routingInfo && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Payment Rail</span>
                <span className="font-medium text-foreground capitalize">
                  {routingInfo?.selectedRail?.replace('-', ' ')}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total Fees</span>
                <span className="font-medium text-foreground">
                  ${routingInfo?.totalFee?.toFixed(2)}
                </span>
              </div>
            </>
          )}
          
          <div className="pt-3 border-t border-border">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Transaction Hash</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono bg-muted px-2 py-1 rounded text-foreground">
                0x7a8f9d2e4c1b6f3a8e5d2c9b7f4a1e8d6c3b9f7e5a2d8c6b4f1a9e7d5c3b1f
              </code>
              <button className="text-primary hover:text-primary/80">
                <Icon name="Copy" size={16} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Confirmations</span>
            <div className="flex items-center gap-1">
              <Icon name="CheckCircle" size={14} className="text-success" />
              <span className="font-medium text-success">
                {routingInfo?.confirmations || 12} / {routingInfo?.confirmations || 12}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Settlement Time</span>
            <span className="font-medium text-foreground">
              {routingInfo?.estimatedTime || '< 2 min'}
            </span>
          </div>
        </div>
      )}

      {/* Error Details */}
      {!isSuccess && (
        <div className="p-4 rounded-lg bg-error/10 border border-error/20 text-left">
          <div className="flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">Error Details</p>
              <p className="text-xs text-muted-foreground">
                Insufficient funds in wallet or network congestion. Please try again or contact support.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3 pt-4">
        {isSuccess ? (
          <>
            <Button
              variant="default"
              size="lg"
              fullWidth
              iconName="Download"
              iconPosition="left"
            >
              Download Receipt
            </Button>
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={onReset}
            >
              Make Another Payment
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="default"
              size="lg"
              fullWidth
              onClick={onReset}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              size="lg"
              fullWidth
              iconName="MessageCircle"
              iconPosition="left"
            >
              Contact Support
            </Button>
          </>
        )}
      </div>

      {/* Security Notice */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-4 border-t border-border">
        <Icon name="Shield" size={14} />
        <span>Transaction secured by blockchain technology</span>
      </div>
    </div>
  );
};

export default PaymentStatus;